import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _extends from "@babel/runtime/helpers/extends";
var _class, _class2, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _AbDialog;
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { AbPopupContext } from "abreact/incubator/ui/popup/PopupContext";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbDialogContent } from "abreact/popup/AbDialogContent";
import { DialogActionState, DialogContext } from "abreact/popup/DialogContext";
import * as AbHooks from "abreact/utils/AbHooks";
import { AbResizeObserver } from "abweb/utils/geometry/AbResizeObserver";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { action, makeObservable, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
export let CancelSource = /*#__PURE__*/function (CancelSource) {
  CancelSource[CancelSource["CANCEL_BUTTON"] = 0] = "CANCEL_BUTTON";
  CancelSource[CancelSource["OTHER"] = 1] = "OTHER";
  return CancelSource;
}({});

/**
 * Properties of the Dialog component.
 */

const AbDialogContentWrapper = observer(props => {
  const popupContext = React.useContext(AbPopupContext);
  if (!popupContext) throw new Error("dialog content must be rendered in popup");
  const dialogContentRef = React.useRef(null);
  const dialogActionState = AbHooks.useLazyRef(() => new DialogActionState());
  const dialogCallbacks = AbHooks.useLazyRef(() => new AbDialogCallbacks({
    onCancel: props.onCancel,
    popupHandle: popupContext.popupHandle
  }, dialogActionState.current));
  dialogCallbacks.current.onCancel = props.onCancel;
  const titleID = `ab-dialog-title-${PopupUtils.getPopupId(popupContext.popupHandle)}`;
  React.useImperativeHandle(props.abRef, () => ({
    dialogContent: dialogContentRef.current,
    dialogCallbacks: dialogCallbacks.current,
    titleID: titleID
  }));
  const onSubmit = async () => {
    if (props.onSubmit) {
      dialogActionState.current.setWaitingForPromise(true);
      return props.onSubmit().then(canClose => {
        if (canClose) {
          popupContext.popupHandle.close();
        }
        return canClose;
      }).finally(() => {
        dialogActionState.current.setWaitingForPromise(false);
      });
    } else {
      popupContext.popupHandle.close();
      return true;
    }
  };
  const close = () => dialogCallbacks.current.cancel(CancelSource.OTHER);
  return /*#__PURE__*/React.createElement(DialogContext.Provider, {
    value: {
      dialogCallbacks: dialogCallbacks.current,
      dialogActionState: dialogActionState.current
    }
  }, /*#__PURE__*/React.createElement(AbPopupContext.Provider, {
    value: {
      popupHandle: {
        close: close,
        untilClosed: popupContext.popupHandle.untilClosed
      }
    }
  }, /*#__PURE__*/React.createElement(AbDialogContent, _extends({}, props, {
    titleID: titleID,
    onSubmit: props.onSubmit ? onSubmit : undefined,
    onMaximize: props.toggleMaximized,
    isMaximized: props.isMaximized,
    ref: dialogContentRef
  }))));
});
AbDialogContentWrapper.displayName = "AbDialogContentWrapper";

/**
 * The **AbDialog** component is a modal popup dialog for conducting a user interaction.
 *
 * [[include:abreact/public/popup/AbDialog.md]]
 */
export let AbDialog = observer(_class = (_class2 = (_AbDialog = class AbDialog extends React.Component {
  constructor(abDialogProps) {
    super(abDialogProps);
    // The currently recorded width & height. null means "not recorded".
    _initializerDefineProperty(this, "dialogWidth", _descriptor, this);
    _initializerDefineProperty(this, "dialogHeight", _descriptor2, this);
    _initializerDefineProperty(this, "isMaximized", _descriptor3, this);
    _initializerDefineProperty(this, "showBars", _descriptor4, this);
    _initializerDefineProperty(this, "userHasResized", _descriptor5, this);
    _initializerDefineProperty(this, "contentWrapperRef", _descriptor6, this);
    _defineProperty(this, "contentResizeObserver", new AbResizeObserver((_, element) => this.onResizeContent(element)));
    _defineProperty(this, "onResizeContent", action(element => {
      const scrollHeight = element.scrollHeight;
      const offsetHeight = element.offsetHeight;
      this.showBars = scrollHeight > offsetHeight;
    }));
    _defineProperty(this, "rootResizeObserver", new AbResizeObserver((_, element) => this.onResizeRoot(element)));
    _defineProperty(this, "onResizeRoot", action(element => {
      if (!this.props.keepDialogSize || this.userHasResized || this.isMaximized) return;
      // remember the value and cause a re-render by changing observables
      this.dialogWidth = element.offsetWidth;
      this.dialogHeight = element.offsetHeight;
    }));
    makeObservable(this);
    this.dialogWidth = null;
    this.dialogHeight = null;
    this.isMaximized = abDialogProps.maximizable === true ? abDialogProps.defaultMaximized ?? false : false;
    this.showBars = false;
    this.userHasResized = false;
    this.contentWrapperRef = null;
  }
  render() {
    const hasTitle = Boolean(this.props.title);
    const hasHeader = Boolean(hasTitle || this.props.icon || this.props.closeButton || this.props.maximizable);
    const classes = ReactUtils.componentClassNamesString("ab-dialog", this.props, [hasHeader ? "ab-dialog-has-header" : undefined, this.props.flushContent ? "ab-dialog-flush-content" : undefined, this.showBars ? "ab-content-is-scrolling" : undefined]);
    const overlayClassnames = ReactUtils.classNamesString(classes.split(" ").filter(value => value.indexOf("ab-overlay-class") >= 0));
    const style = this.props.style ?? {};
    // If client asked us to: we've measured the width or height, pass it to the popup's style/
    if (this.dialogWidth) style.width = this.dialogWidth;
    if (this.dialogHeight) style.height = this.dialogHeight;
    const onKeyDown = event => {
      const handled = this.props.onKeyDown?.(event);
      if (handled) {
        return true;
      }
      if (event.key === KeyValue.ESCAPE && this.contentWrapperRef) {
        event.preventDefault();
        event.stopPropagation();
        this.contentWrapperRef.dialogCallbacks.cancel(CancelSource.OTHER);
        return true;
      }
      return false;
    };

    // Note that the onClose function here falls back to an empty function. This was done to avoid error logging with
    // declarative dialogs that don't have an onClose callback. AbPopup logs errors in this case, but we believe that
    // dialogs should have an onOK or onCancel handler, and that onClose is not really necessary in those cases.
    // todo: consider removing onClose altogether for AbDialogs.
    return /*#__PURE__*/React.createElement(AbPopup, {
      id: this.props.id,
      className: classes,
      style: style,
      flushContent: true,
      role: "dialog",
      ariaLabelledByID: this.contentWrapperRef?.titleID,
      origin: this.props.origin,
      position: this.props.position,
      positioner: this.props.positioner,
      draggable: this.props.draggable,
      draggableElementSelector: ".ab-dialog-title-bar",
      isMaximized: this.isMaximized && this.props.maximizable,
      resizable: this.props.resizable,
      onResized: () => {
        runInAction(() => this.userHasResized = true);
        this.props.onResized && this.props.onResized();
      },
      resizeBoxRef: boxRef => {
        if (!boxRef) this.rootResizeObserver.disconnect();else this.rootResizeObserver.observe(boxRef.element);
      },
      modal: this.props.modal,
      overlayClassnames: overlayClassnames,
      overlay: this.props.modal,
      closeOnBackgroundClick: false,
      focus: this.props.focus,
      setFocus: this.props.setFocus,
      onKeyDown: onKeyDown,
      onKeyPress: this.props.onKeyPress,
      onClose: this.props.onClose ?? TypeUtils.emptyFunction,
      dataTestId: this.props.dataTestId
    }, /*#__PURE__*/React.createElement(AbDialogContentWrapper, _extends({}, this.props, {
      toggleMaximized: action(() => {
        if (this.props.maximizable) this.isMaximized = !this.isMaximized;
      }),
      isMaximized: this.isMaximized,
      abRef: action(ref => {
        this.contentWrapperRef = this.contentWrapperRef ? this.contentWrapperRef : ref;
      }),
      resizeObserver: this.contentResizeObserver
    })));
  }
}, _defineProperty(_AbDialog, "defaultProps", {
  modal: true,
  okButton: true,
  closeButton: true,
  resizable: true,
  draggable: true,
  focus: true
}), _AbDialog), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "dialogWidth", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "dialogHeight", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "isMaximized", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class2.prototype, "showBars", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class2.prototype, "userHasResized", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class2.prototype, "contentWrapperRef", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class2)) || _class;
class AbDialogCallbacks {
  constructor(props, dialogActionState) {
    _defineProperty(this, "props", void 0);
    _defineProperty(this, "dialogActionState", void 0);
    _defineProperty(this, "ok", () => {
      this.props.popupHandle.close();
    });
    _defineProperty(this, "submit", () => {
      this.props.popupHandle.close();
    });
    _defineProperty(this, "cancel", source => {
      if (this.props.onCancel) {
        this.dialogActionState.setWaitingForPromise(true);
        this.props.onCancel(source).then(shouldClose => {
          shouldClose && this.props.popupHandle.close();
        }).finally(() => {
          this.dialogActionState.setWaitingForPromise(false);
        });
      } else {
        this.props.popupHandle.close();
      }
    });
    this.props = props;
    this.dialogActionState = dialogActionState;
  }
  set onCancel(cb) {
    this.props.onCancel = cb;
  }
}
//# sourceMappingURL=AbDialog.js.map
